import type { PadScreenData, PadStandings } from '@starpoint/engine';
import { isErasedPlayer } from '../playerName';

// ─────────────────────────────────────────────────────────────────────
// THE PROJECTION, TURNED BACK INTO WHAT THE RENDERERS TAKE (#185).
//
// The pad components each take a tournament- or series-shaped object. That is
// not an accident to be refactored away here: they are the SAME components
// Sentralen renders, and rewriting five of them to consume a new shape inside
// an access-control change is how a migration quietly becomes a redesign.
//
// So the projection carries this court's own data in its existing shapes, and
// this rebuilds a session object from it. The result looks like a tournament
// and contains ONE COURT: its matches, its divisions' pairs, the players those
// name, today's breaks. What genuinely spans courts — the match numbering and
// the two standings tables — arrives already computed and is handed to the
// renderers through their `projected` props (#325, #328).
//
// WHAT THIS IS NOT: a second implementation of the projection. It reshapes what
// the server sent and computes nothing. If a value is missing here, the fix is
// in `buildPadScreen`, not a local recomputation — a pad that can recompute a
// club-wide total is a pad that was sent club-wide data.
// ─────────────────────────────────────────────────────────────────────

type Row = Record<string, unknown>;

export interface PadRendererProps {
  tournament: Row | null;
  activeSeries: Row | null;
  forceTournament: boolean;
  forceSeries: boolean;
  /** The enkeltmatch activity the pad renders, when the court has one. */
  forceSingleMatch: Row | null;
  /**
   * The id a RESULT or a LIVE SCORE names for this court — the linked
   * BOOKING's for an enkeltmatch, the match's for every other format.
   *
   * The distinction is not cosmetic. The resolver puts the booking in
   * `current` for a single match, so that is what the command's scope check
   * compares, while the renderer draws the ACTIVITY. Reading the id off the
   * thing on screen would name the activity and be refused.
   */
  submissionMatchId: string | null;
  bookings: Row[];
  courtMeta: Row;
  allPlayers: Row[];
  projected: {
    globalMatchNumber: Record<string, number> | null;
    clubStandings: { standings: Row; leader: Row | null } | null;
    serieStandings: Row[] | null;
  };
}

/** The server's ordered table, rebuilt into the shape SeriePadBanner renders. */
function serieRows(standings: PadStandings, pairsById: Map<string, Row>): Row[] | null {
  if (!standings || standings.kind !== 'serie') return null;
  return standings.rows.map((r) => ({
    // `pair` carries p1Id/p2Id so the banner's own label function works
    // unchanged; the projected `label` is kept as a fallback for a pair whose
    // players were not projected.
    pair: pairsById.get(r.pairId) ?? { id: r.pairId, name: r.label },
    pos: r.pos,
    points: r.points,
    played: r.played,
    wins: r.wins,
    losses: r.losses,
    setDiff: r.setDiff,
    gameDiff: r.gameDiff,
  }));
}

/** The server's club totals, rebuilt into what ClubPadBanner reads. */
function clubScoreboard(standings: PadStandings): { standings: Row; leader: Row | null } | null {
  if (!standings || standings.kind !== 'club') return null;
  const byId: Row = {};
  for (const c of standings.clubs) {
    byId[c.clubId] = {
      clubId: c.clubId,
      name: c.name,
      wins: c.wins,
      losses: c.losses,
      ties: c.ties,
      totalScore: c.totalScore,
      opponentScore: c.opponentScore,
      matchesPlayed: c.matchesPlayed,
      finalsPlayed: 0,
    };
  }
  const leader = standings.leaderClubId ? ((byId[standings.leaderClubId] as Row) ?? null) : null;
  return { standings: byId, leader };
}

/**
 * The projected players, with an erased player's empty name replaced by the
 * label the reader sees (#1321).
 *
 * The renderers are Sentralen's own components and read `player.name` in
 * dozens of places — the scoreboard, the pair labels, the standings. Naming the
 * player once, HERE, is what reaches all of them; teaching each renderer the
 * flag would be five components changed to say one thing. Display only: the
 * label is in the reader's language, chosen in the browser, and never leaves
 * the pad (CLAUDE.md, #310a). `erased` stays on the row.
 */
function labelErased(players: Row[], erasedLabel: string | undefined): Row[] {
  if (!erasedLabel) return players;
  return players.map((p) => (isErasedPlayer(p) ? { ...p, name: erasedLabel } : p));
}

/**
 * Build the pad renderers' props from one court's projection.
 *
 * `mode` decides which slot the session goes in, and the matching force flag is
 * set: the SERVER already decided what belongs on this court, force included,
 * so the client must not re-litigate it from a date and a clock it may read
 * differently.
 */
export function padScreenProps(
  view: {
    court: number | string;
    mode: string;
    screen: PadScreenData | null;
  },
  /** «Slettet spiller» in the reader's language — see `labelErased`. */
  erasedLabel?: string,
): PadRendererProps {
  const empty: PadRendererProps = {
    tournament: null,
    activeSeries: null,
    forceTournament: false,
    forceSeries: false,
    forceSingleMatch: null,
    submissionMatchId: null,
    bookings: [],
    courtMeta: {},
    allPlayers: [],
    projected: { globalMatchNumber: null, clubStandings: null, serieStandings: null },
  };
  const screen = view.screen;
  if (!screen) return empty;

  const courtMeta: Row = screen.courtMeta ? { [String(view.court)]: screen.courtMeta } : {};
  const players = labelErased(screen.players as unknown as Row[], erasedLabel);

  // ── ENKELTMATCH ─────────────────────────────────────────────────────
  if (view.mode === 'single-match' && screen.singleMatch) {
    const { activity, booking } = screen.singleMatch;
    // The resolver finds the linked booking in `bookings` by
    // `seriesId === activity.id` on this court, so it has to be in the list it
    // is given. It usually already is — it is one of today's — but relying on
    // that would make the pad depend on the booking also being *today* in the
    // projection's sense.
    // AND THE LINK THE RESOLVER LOOKS FOR MUST EXIST.
    //
    // The pad re-resolves its own court with `forceSingleMatch: activity`,
    // and banepadResolveCourt's forced branch links an ACTIVITY to its booking
    // by `booking.seriesId === activity.id`. A plan-based enkeltmatch has no
    // such link: the plan owns `bookingId`, and the booking's `seriesId` is
    // empty. The server resolves it through the plannedMatches branch instead,
    // so the payload was correct and the pad still drew a plain «reserved»
    // booking card — seen on a physical tablet (#216).
    //
    // Stamping the link here is display-only and local to the pad's props: for
    // an activity-based enkeltmatch the value is already exactly this, so both
    // kinds take one path.
    const linked = { ...booking, seriesId: String(activity.id ?? '') };
    const bookings = [
      linked,
      ...screen.bookings.filter((b) => String(b.id ?? '') !== String(booking.id ?? '')),
    ];
    return {
      ...empty,
      forceSingleMatch: activity,
      // THE BOOKING's id — see submissionMatchId.
      submissionMatchId: String(booking.id ?? '') || null,
      bookings,
      courtMeta,
      allPlayers: players,
    };
  }

  if (!screen.session) {
    return { ...empty, bookings: screen.bookings, courtMeta, allPlayers: players };
  }

  const cfg = screen.config;
  const session: Row = {
    ...screen.session,
    config: {
      padFlowMode: cfg?.padFlowMode ?? 'result',
      // One day entry, because a pad only ever renders today.
      days: cfg?.day ? [cfg.day] : [],
      classes: cfg?.classes ?? [],
      clubs: cfg?.clubs ?? [],
    },
    players,
    matches: screen.matches,
    pairs: screen.pairs,
  };

  const globalMatchNumber: Record<string, number> = {};
  for (const m of screen.matches) {
    const id = String(m.id ?? '');
    const n = m.padMatchNumber;
    if (id && typeof n === 'number') globalMatchNumber[id] = n;
  }

  const pairsById = new Map(screen.pairs.map((p) => [String(p.id), p]));
  const isSeries = view.mode === 'series';

  return {
    tournament: isSeries ? null : session,
    activeSeries: isSeries ? session : null,
    forceSingleMatch: null,
    // Null for every other format, and not an oversight: those renderers hand
    // the match id to the callback themselves (`onRecordResult(matchId, …)`,
    // `updatedMatch.id`). Only the enkeltmatch pad hands back an ACTIVITY,
    // which is not what the command compares.
    submissionMatchId: null,
    // The server resolved this court; the pad renders what it was told.
    forceTournament: !isSeries,
    forceSeries: isSeries,
    bookings: screen.bookings,
    courtMeta,
    allPlayers: players,
    projected: {
      globalMatchNumber: Object.keys(globalMatchNumber).length ? globalMatchNumber : null,
      clubStandings: clubScoreboard(screen.standings),
      serieStandings: serieRows(screen.standings, pairsById),
    },
  };
}
